import { memo, ReactNode, forwardRef, useCallback } from 'react'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import LinearProgress from '@mui/material/LinearProgress'
import ButtonGroup from '@mui/material/ButtonGroup'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// eslint-disable-next-line react/display-name
const StripeStyleCounterInput = forwardRef(
  (
    {
      name,
      value,
      onBlur,
      onChange,
      label,
      disabled,
      error,
      helperText,
      loading,
      fullWidth,
    }: {
      value: number
      name: string
      label: string
      error: boolean
      disabled: boolean
      loading: boolean
      helperText: ReactNode
      onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>
      onChange?: (value: number) => void
      fullWidth?: boolean
    },
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> =
      useCallback(
        event => {
          if (!onChange) {
            return
          }
          const value = parseInt(event.target.value)
          onChange(Number.isNaN(value) || value < 0 ? 0 : value)
        },
        [onChange],
      )
    const handleIncrement = useCallback(() => {
      if (!onChange) {
        return
      }
      onChange(value + 1)
    }, [onChange, value])
    const handleDecrement = useCallback(() => {
      if (!onChange) {
        return
      }
      onChange(Math.max(value - 1, 0))
    }, [onChange, value])
    return (
      <Box
        sx={{
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '0.93rem',
              marginBottom: 0.5,
              lineHeight: '1.15',
              color: '#30313d',
            }}
          >
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', width: fullWidth ? '100%' : 'auto' }}>
          <Box
            sx={{
              display: 'inline-block',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: 1,
              width: fullWidth ? '100%' : 'auto',
            }}
          >
            <ButtonGroup
              sx={{
                width: fullWidth ? '100%' : 'auto',
              }}
            >
              <Button
                sx={theme => ({
                  color: theme.palette.getContrastText(theme.palette.secondary[200]),
                  backgroundColor: error
                    ? 'white'
                    : disabled
                      ? theme.palette.secondary[100]
                      : theme.palette.secondary[200],
                  borderColor: error ? '#df1b41' : theme.palette.text.primary,
                  borderWidth: 2,
                  borderRightWidth: '0px',
                  '&:hover': {
                    backgroundColor: error ? 'white' : theme.palette.secondary[100],
                  },
                  '&:disabled': {
                    borderColor: error ? '#df1b41' : theme.palette.text.primary,
                    borderWidth: 2,
                    borderRightWidth: '0px',
                  },
                })}
                onClick={handleDecrement}
                disabled={disabled || value <= 0}
              >
                <RemoveIcon
                  fontSize="small"
                  sx={theme => ({
                    color: error ? theme.palette.error.dark : theme.palette.text.primary,
                  })}
                />
              </Button>
              <TextField
                ref={ref}
                fullWidth={fullWidth}
                onBlur={onBlur}
                onChange={handleChange}
                name={name}
                disabled={disabled}
                sx={theme => ({
                  '& .MuiOutlinedInput-root': {
                    height: '44px',
                    marginRight: '1px',
                    marginLeft: '-1px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      marginTop: '0.5px',
                      borderRadius: 0,
                      borderColor: `${error ? '#df1b41' : theme.palette.text.primary}!important`,
                      borderWidth: 2,
                    },
                    '&:hover fieldset': {
                      borderColor: `${error ? '#df1b41' : theme.palette.text.primary}!important`,
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: `${error ? '#df1b41' : theme.palette.text.primary}!important`,
                      borderWidth: 2,
                    },
                    '& input': {
                      textAlign: 'center',
                      color: error ? theme.palette.error.dark : theme.palette.text.primary,
                      borderWidth: 2,
                      width: fullWidth ? '100%' : '60px',
                    },
                  },
                })}
                size="small"
                value={value}
              />
              <Button
                sx={theme => ({
                  color: theme.palette.getContrastText(theme.palette.secondary[200]),
                  backgroundColor: error
                    ? 'white'
                    : disabled
                      ? theme.palette.secondary[100]
                      : theme.palette.secondary[200],
                  borderColor: error ? '#df1b41' : theme.palette.text.primary,
                  borderWidth: 2,
                  borderLeftWidth: '0px',
                  '&:hover': {
                    backgroundColor: error ? 'white' : theme.palette.secondary[100],
                  },
                  '&:disabled': {
                    borderColor: error ? '#df1b41' : theme.palette.text.primary,
                    borderWidth: 2,
                    borderLeftWidth: '0px',
                  },
                })}
                onClick={handleIncrement}
                disabled={disabled}
              >
                <AddIcon
                  fontSize="small"
                  sx={theme => ({
                    color: error ? theme.palette.error.dark : theme.palette.text.primary,
                  })}
                />
              </Button>
            </ButtonGroup>
            {loading && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 2,
                  left: 2,
                  right: 2,
                  borderBottomRightRadius: 2,
                  borderBottomLeftRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <LinearProgress
                  color={error ? 'error' : 'inherit'}
                  sx={{
                    height: '2px',
                    // backgroundColor: error ? 'error' : theme.palette.common.white,
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            marginBottom: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.93rem',
              marginTop: 0.5,
              lineHeight: '1.15',
              color: error ? '#df1b41' : '#30313d',
            }}
          >
            {helperText}
          </Typography>
        </Box>
      </Box>
    )
  },
)

export default memo(StripeStyleCounterInput)
